import React from 'react';
import { type Dispatch } from 'redux';
import { type TFunction } from 'i18next';
import { useDispatch } from 'react-redux';
import { useIntl, type IntlShape } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from '@fuse/hooks';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { type FabType } from '../page-layouts/components/PageFab';
import useSettings from './useSettings';
import useUserData from './useUserData';
import useIsModuleDisabled from './useIsModuleDisabled';

export interface BaseProps {
	/** The Redux dispatcher function */
	dispatch: Dispatch<any>;
	/** The application settings */
	sysSettings: any;
	/** The user data */
	userData: any;
	/** The function used to check if a module is disabled */
	isModuleDisabled: (module: string) => boolean;
	/** The authorizer */
	authorizer: any;
	/** The function used to handle internationalization */
	intl: IntlShape;
	/** The function used to handle translations */
	t: TFunction;
}

export type TabProps = {
	/** The settings instance */
	settings: any;
	/** The details model */
	model?: any;
	/** If true the tab can be modified */
	isEdit?: boolean;
	/** The function used to refresh the container */
	updateRefresh: VoidFunction;
};

export interface TabType<T = any> {
	id: string;
	title: string;
	disabled?: (tabProps: BaseProps & TabProps) => boolean | undefined;
	hidden?: (tabProps: BaseProps & TabProps) => boolean;
	content: (contentProps: T) => React.ReactNode;
}

type UseTabProps<T = any> = {
	baseUrl: string;
	tabs: TabType<T>[];
	fabs?: FabType<any>[];
	tabProps: TabProps;
	fabProps?: any;
	currentTabId?: string;
};

export default function useTabs<T = any>(props: UseTabProps<T>) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();
	const intl = useIntl();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);
	const navigate = useNavigate();
	const location = useLocation();

	const baseProps: BaseProps = { dispatch, sysSettings, isModuleDisabled, authorizer, userData, intl, t };
	const tabProps: BaseProps & TabProps = { ...baseProps, ...props.tabProps };
	const fabProps: BaseProps = { ...baseProps, ...props.fabProps };

	const visibleTabs = props.tabs?.filter(tab => !tab.hidden || !tab.hidden(tabProps)) || [];
	const visibleFabs = props.fabs && props.fabProps ? props.fabs.filter(fab => !fab.hidden?.(fabProps)) : [];

	const currentTab = visibleTabs.find(tab => tab.id === props.currentTabId);
	const currentTabFab = visibleFabs.find(fab => fab.id === props.currentTabId);

	const changeTab = React.useCallback(
		(newTabId: string, replace?: boolean) => {
			navigate(`${props.baseUrl}/${newTabId}${location.search}`, { replace });
		},
		[location.search, navigate, props.baseUrl]
	);

	useDeepCompareEffect(() => {
		if (visibleTabs.length > 0 && !visibleTabs.find(tab => props.currentTabId === tab.id)) {
			changeTab(visibleTabs[0].id, true);
		}
	}, [visibleTabs]);

	return {
		visibleTabs,
		visibleFabs,
		currentTab,
		currentTabFab,
		changeTab
	};
}
